import { useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  faCircleCheck,
  faCircleXmark,
  faPenToSquare,
  faTrashXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faCoins, faPlus } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { AdminSection, Cluster, OutletContext } from '@typings';
import { userSelector } from '@selectors';
import { useSelector } from '@hooks';
import { getFormattedMemory, getGpuFromResourcePreset } from '@utils';

import { Button, Icon, Modal, Render, Table } from '@components';
import { AdminSectionClusterTabButton } from '@components/AdminSection';
import {
  ClusterDeleteResourcePresetModal,
  ClusterResourcePresetModal,
  ManageClusterStorageVolumesCreditsModal,
} from '@components/Cluster';
import { GpuModels } from '@components/Ui';

type HeaderCell = {
  text: string;
  className?: string;
};

const HEADERS: HeaderCell[] = [
  { text: 'Name', className: 'pl-10' },
  { text: 'CPU' },
  { text: 'Memory' },
  { text: 'GPU Devices' },
  { text: 'Resource Pools' },
  { text: 'Round-Robin' },
  { text: 'Preemptability' },
  { text: 'Credits / Hour' },
  { text: '', className: 'pr-10' },
];

export const AdminSectionClusterResourcesPage = () => {
  const { cluster, setCluster, setRightTabComponent } =
    useOutletContext<OutletContext.AdminSectionCluster>();

  const { systemRole } = useSelector(userSelector);

  const handleAddResourcePreset = useCallback(
    (newResourcePreset: Cluster.ResourcePreset) => {
      setCluster((prev) => {
        prev.orchestrator.resourcePresets = [
          ...(prev.orchestrator.resourcePresets ?? []),
          newResourcePreset,
        ];
      });
    },
    [setCluster],
  );

  const handleUpdateResourcePreset = useCallback(
    (previousPresetName: string) =>
      (resourcePresetParams: Partial<Cluster.ResourcePreset>) => {
        setCluster((prev) => {
          prev.orchestrator.resourcePresets =
            prev.orchestrator.resourcePresets?.map((preset) =>
              preset.name === previousPresetName
                ? { ...preset, ...resourcePresetParams }
                : preset,
            ) ?? [];
        });
      },
    [setCluster],
  );

  const handleRemoveResourcePreset = useCallback(
    (name: string) => () => {
      setCluster((prev) => {
        prev.orchestrator.resourcePresets =
          prev.orchestrator.resourcePresets?.filter(
            (preset) => preset.name !== name,
          ) ?? [];
      });
    },
    [setCluster],
  );

  const handleStorageVolumesUpdate = useCallback(
    (storageVolume: Cluster.StorageVolume) => {
      setCluster((prev) => {
        prev.storage.volumes = prev.storage.volumes.map((volume) =>
          volume.name === storageVolume.name
            ? { ...volume, ...storageVolume }
            : volume,
        );
      });
    },
    [setCluster],
  );

  useEffect(() => {
    if (systemRole !== AdminSection.Role.Admin) {
      return;
    }

    if (!cluster.orchestrator.resourcePresets?.length) {
      return;
    }

    setRightTabComponent(
      <div className="flex gap-3">
        <Modal
          content={
            <ManageClusterStorageVolumesCreditsModal
              clusterName={cluster.name}
              storageVolumes={cluster.storage.volumes}
              onStorageVolumesUpdate={handleStorageVolumesUpdate}
            />
          }
        >
          <AdminSectionClusterTabButton
            icon={faCoins}
            title="Manage Storage Credits"
          />
        </Modal>

        <Modal
          content={
            <ClusterResourcePresetModal
              clusterName={cluster.name}
              onAddResourcePreset={handleAddResourcePreset}
              nodePools={cluster.orchestrator.resourcePoolTypes}
            />
          }
        >
          <AdminSectionClusterTabButton icon={faPlus} title="Create Preset" />
        </Modal>
      </div>,
    );

    return () => {
      setRightTabComponent(null);
    };
  }, [
    cluster.name,
    cluster.orchestrator.resourcePoolTypes,
    cluster.orchestrator.resourcePresets?.length,
    cluster.storage.volumes,
    handleAddResourcePreset,
    handleStorageVolumesUpdate,
    setCluster,
    setRightTabComponent,
    systemRole,
  ]);

  return (
    <>
      <Table className="w-full auto-rows-min grid-cols-[repeat(4,minmax(max-content,auto))_180px_repeat(3,max-content)_min-content] overflow-auto rounded-none border-b border-stroke">
        <Table.Header>
          <Table.Row>
            {HEADERS.map(({ text, className }) => (
              <Table.Head key={text} className={`bg-background ${className}`}>
                {text}
              </Table.Head>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {cluster.orchestrator.resourcePresets?.map((resourcePreset) => {
            const {
              name,
              cpu,
              memory,
              isPreemptible,
              creditsPerHour,
              schedulerEnabled,
              resourcePoolNames = [],
            } = resourcePreset;

            const gpuModels = getGpuFromResourcePreset(resourcePreset, {
              withPlaceholder: true,
            });

            return (
              <Table.Row key={name}>
                <Table.Cell className="pl-10">{name}</Table.Cell>

                <Table.Cell>{cpu}</Table.Cell>

                <Table.Cell>{getFormattedMemory(memory)}</Table.Cell>

                <Table.Cell>
                  <GpuModels models={gpuModels} />
                </Table.Cell>

                <Table.Cell>
                  <Render if={resourcePoolNames}>
                    <p>{resourcePoolNames?.join(', ')}</p>
                  </Render>
                </Table.Cell>

                <Table.Cell>
                  <div className="flex justify-center">
                    <Icon
                      icon={schedulerEnabled ? faCircleCheck : faCircleXmark}
                      className={clsx('text-[20px]', {
                        'text-error': !schedulerEnabled,
                        'text-success': schedulerEnabled,
                      })}
                    />
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <div className="flex justify-center">
                    <Icon
                      className={clsx('text-[20px]', {
                        'text-error': !isPreemptible,
                        'text-success': isPreemptible,
                      })}
                      icon={isPreemptible ? faCircleCheck : faCircleXmark}
                    />
                  </div>
                </Table.Cell>

                <Table.Cell>{creditsPerHour}</Table.Cell>

                <Table.Cell className="flex-row items-center pr-10">
                  <Render if={systemRole === AdminSection.Role.Admin}>
                    <div className="flex place-items-center gap-3">
                      <Modal
                        content={
                          <ClusterResourcePresetModal
                            edit
                            name={name}
                            clusterName={cluster.name}
                            resourcePreset={resourcePreset}
                            nodePools={cluster.orchestrator.resourcePoolTypes}
                            onResourcePresetUpdate={handleUpdateResourcePreset(
                              name,
                            )}
                          />
                        }
                      >
                        <Button
                          variant="ghost"
                          className="h-6 w-6 p-0 text-[18px]"
                        >
                          <Icon icon={faPenToSquare} />
                        </Button>
                      </Modal>

                      <span className="h-4 border-l" />

                      <Modal
                        content={
                          <ClusterDeleteResourcePresetModal
                            name={name}
                            onResourcePresetRemove={handleRemoveResourcePreset(
                              name,
                            )}
                          />
                        }
                      >
                        <Button
                          variant="ghost-error"
                          className="h-6 w-6 p-0 text-[18px]"
                        >
                          <Icon icon={faTrashXmark} />
                        </Button>
                      </Modal>
                    </div>
                  </Render>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <Render if={!cluster.orchestrator.resourcePresets?.length}>
        <p className="break-words py-20 text-center text-neural-04">
          No resource presets found
        </p>
      </Render>
    </>
  );
};
